import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import axios from 'axios';
import { RootState } from 'state/store';
import { NewTask } from 'types/store/newTask';
import { createNewTask } from 'api/newTask';
import { deleteTask } from 'api/taskDetail';
import { reorderTasks } from 'api/tasksList';
import { SnackBarNotificationParams } from 'Hooks/useSnackbarNotification';

interface ProjectTasksListActionsState {
  taskIdToEdit?: string;
  taskIdToDuplicate?: string;
  shouldRefreshTasks?: boolean;
  snackBarNotification?: SnackBarNotificationParams | null;
  openFiltersSidePanel?: boolean;
  bulkActionsNotifications?: SnackBarNotificationParams[];
}

interface CreateNewTaskParams {
  newTasks: NewTask[];
  userId: string;
  sendNotifications?: boolean;
}

const initialState: ProjectTasksListActionsState = {};

export const createProjectTask = createAsyncThunk(
  'projectTasksList/createProjectTask',
  async (
    { newTasks, sendNotifications = true }: CreateNewTaskParams,
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await createNewTask({
        newTasks,
        sendNotifications,
      });
      dispatch(setShouldRefreshTasks(true));
      return response.successes[0];
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const deleteProjectTask = createAsyncThunk(
  'projectTasksList/deleteProjectTask',
  async (taskId: string, { rejectWithValue }) => {
    try {
      const response = await deleteTask(taskId);
      return response;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data);
      }
      return rejectWithValue(error);
    }
  }
);

interface UpdateProjectTaskOrderParams {
  projectId: string;
  taskId: string;
  newOrder: number;
}

export const updateProjectTaskOrder = createAsyncThunk(
  'projectTasksList/updateProjectTaskOrder',
  async (params: UpdateProjectTaskOrderParams, { rejectWithValue }) => {
    try {
      await reorderTasks(params);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data);
      }
      return rejectWithValue('Error reordering tasks');
    }
  }
);

const actionsSlice = createSlice({
  name: 'projectTasksList/actions',
  initialState,
  reducers: {
    setBulkActionsNotifications(state, action) {
      state.bulkActionsNotifications = action.payload;
    },
    setTaskIdToEdit(state, action) {
      state.taskIdToEdit = action.payload;
    },
    setTaskIdToDuplicate(state, action) {
      state.taskIdToDuplicate = action.payload;
    },
    setShouldRefreshTasks(state, action) {
      state.shouldRefreshTasks = action.payload;
    },
    setSnackBarNotification(state, action) {
      state.snackBarNotification = action.payload;
    },
    toggleFiltersSidePanel(state) {
      state.openFiltersSidePanel = !state.openFiltersSidePanel;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createProjectTask.rejected, (state, action) => {
        throw new Error(action.payload as string);
      })
      .addCase(deleteProjectTask.rejected, (state, action) => {
        throw new Error(action.payload as string);
      })
      .addCase(updateProjectTaskOrder.rejected, (state, action) => {
        throw new Error(action.payload as string);
      });
  },
});

const selectAllProjectTasksListActions = (state: RootState) =>
  state.projectTasksList.actions;

export const selectTaskIdToEdit = createSelector(
  [selectAllProjectTasksListActions],
  (actions: ProjectTasksListActionsState) => actions.taskIdToEdit
);

export const selectTaskIdToDuplicate = createSelector(
  [selectAllProjectTasksListActions],
  (actions: ProjectTasksListActionsState) => actions.taskIdToDuplicate
);

export const selectShouldRefreshTasks = createSelector(
  [selectAllProjectTasksListActions],
  (actions: ProjectTasksListActionsState) => actions.shouldRefreshTasks ?? false
);

export const selectSnackBarNotification = createSelector(
  [selectAllProjectTasksListActions],
  (actions: ProjectTasksListActionsState) =>
    actions.snackBarNotification ?? null
);

export const selectIsFiltersSidePanelOpen = (state: RootState) =>
  state.projectTasksList.actions.openFiltersSidePanel ?? false;

export const selectBulkActionsNotifications = createSelector(
  [selectAllProjectTasksListActions],
  (actions: ProjectTasksListActionsState) =>
    actions.bulkActionsNotifications ?? []
);

export const {
  setTaskIdToEdit,
  setTaskIdToDuplicate,
  setShouldRefreshTasks,
  setSnackBarNotification,
  toggleFiltersSidePanel,
  setBulkActionsNotifications,
} = actionsSlice.actions;

export default actionsSlice.reducer;
